import { useMemo } from 'react';
import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import TagsController from '../controllers/TagsController';
import commonHandler from '../errorHandlers/common-handler';
import { StoreState } from '../store-interfaces';
import CrudActionContainer from './CrudActionContainer';
import TagModel from '../model/TagModel';

class TagsCRUDActions extends CrudActionContainer {
  getTagsAction: string;
  setTagAction: string;
  deleteTagAction: string;
  assignTagAction: string;
  unassignTagAction: string;

  constructor() {
    super('TAGS');
    this.getTagsAction = super.makeActionKey('GET_TAGS');
    this.setTagAction = super.makeActionKey('SET_TAG');
    this.deleteTagAction = super.makeActionKey('DELETE_TAG');
    this.assignTagAction = super.makeActionKey('ASSIGN_TAG');
    this.unassignTagAction = super.makeActionKey('UNASSIGN_TAG');
  }

  getAll(
    facebookPageId: number,
    skipBusy: boolean = false,
    kinds?: ('otn' | 'regular' | 'service')[],
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!skipBusy) dispatch(this.busy());
      try {
        const tagsArray = await TagsController.getAll(facebookPageId, kinds);

        dispatch({
          type: this.getTagsAction,
          entities: tagsArray,
        });

        return tagsArray;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  create(
    name: string,
    facebookPageId: number,
    kind?: 'otn' | 'regular' | 'service',
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const response = await TagsController.create(
          name,
          facebookPageId,
          kind,
        );

        dispatch({
          type: this.setTagAction,
          entity: response.tag,
        });

        return response.tag;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  delete(
    name: string,
    facebookPageId: number,
    kind: 'otn' | 'regular' | 'service' | undefined,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const response = await TagsController.deleteTag(
          name,
          facebookPageId,
          kind,
        );

        dispatch({
          type: this.deleteTagAction,
          entity: { name, kind },
        });

        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  assign(name: string, facebookPageId: number, contactId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const response = await TagsController.assign(
          name,
          facebookPageId,
          contactId,
        );

        dispatch({
          type: this.assignTagAction,
          entities: {
            tags: response,
            facebookPageId,
            contactId,
          },
        });

        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  unassign(
    name: string,
    kind: 'otn' | 'regular' | 'service' | undefined,
    facebookPageId: number,
    contactId: number,
  ) {
    return async (dispatch: Dispatch<StoreState>): Promise<TagModel[]> => {
      dispatch(this.busy());
      try {
        const response = await TagsController.unassign(
          name,
          kind,
          facebookPageId,
          contactId,
        );
        const tags = await response.json();

        dispatch({
          type: this.unassignTagAction,
          entities: {
            tags: tags || [],
            facebookPageId,
            contactId,
          },
        });

        return tags;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }
}

const tagsCRUDActions = new TagsCRUDActions();

export default tagsCRUDActions;

export const useTagsCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      getAll: (...args: Parameters<typeof tagsCRUDActions.getAll>) =>
        dispatch(tagsCRUDActions.getAll(...args)),
      unassign: (...args: Parameters<typeof tagsCRUDActions.unassign>) =>
        dispatch(tagsCRUDActions.unassign(...args)),
    }),
    [dispatch],
  );
};
